import { useEffect, useState } from "react";
import { Alert } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ModalAtom, SegmentedToggleAtom } from "../../../components/atoms/atom";
import PermissionPresetSelector from "../../../components/project/PermissionPresetSelector";
import {
	isTradeAccessIncomplete,
	normalizePermissions,
} from "../../../constant/projectPermissions";
import { useUpdateMemberPermissionsMutation } from "../../../store/services/projectMembers";
import isGlobalAdminRole from "../../../utils/isGlobalAdmin";
import { showToast } from "../../../utils/method";
import PermissionsForm from "../_shared/permissions-form";

function EditPermissionsModal({
	show,
	onClose,
	member,
	projectId,
	onSuccess,
}) {
	const { t } = useTranslation();
	const globalRole = useSelector((state) => state.auth?.user?.role);
	const isGlobalAdmin = isGlobalAdminRole(globalRole);

	const [permissions, setPermissions] = useState(null);
	const [presetUsedName, setPresetUsedName] = useState(null);
	const [isProjectAdmin, setIsProjectAdmin] = useState(false);

	const [updatePermissions, { isLoading }] =
		useUpdateMemberPermissionsMutation();

	useEffect(() => {
		if (show && member) {
			// Admins carry no permission blob - fall back to the default set so
			// there's a sensible base if the actor switches to Custom.
			setPermissions(normalizePermissions(member.permissions));
			setPresetUsedName(member.presetUsedName || null);
			setIsProjectAdmin(member.isProjectAdmin === true);
		}
	}, [show, member]);

	const handleSave = async () => {
		if (!member?.id) return;
		try {
			await updatePermissions({
				projectId,
				memberId: member.id,
				// In Admin mode the backend stores no blob; we still send one to
				// satisfy validation and to seed a base if later switched.
				permissions: normalizePermissions(permissions),
				presetUsedName,
				...(isGlobalAdmin ? { isProjectAdmin } : {}),
			}).unwrap();
			showToast(t("projects.members.permissions-updated"), {
				type: "success",
			});
			onSuccess?.();
			onClose();
		} catch (error) {
			showToast(error?.data?.message || t("errors.general"), {
				type: "error",
			});
		}
	};

	// A non-global-admin can't change the admin flag, so an admin member is
	// read-only for them (nothing to edit, and we must not store a blob).
	const adminReadOnly = isProjectAdmin && !isGlobalAdmin;

	return (
		<ModalAtom
			show={show}
			handleClose={onClose}
			size="lg"
			title={t("projects.members.edit-permissions")}
			saveText={isLoading ? "common.saving" : "common.save"}
			disabled={isLoading}
			submitDisabled={
				!permissions ||
				adminReadOnly ||
				isTradeAccessIncomplete(permissions)
			}
			handleSubmit={handleSave}
			body={
				permissions && (
					<>
						{isGlobalAdmin && (
							<SegmentedToggleAtom
								className="mb-3"
								ariaLabel={t("projects.members.edit-permissions")}
								value={isProjectAdmin ? "admin" : "custom"}
								onChange={(mode) =>
									setIsProjectAdmin(mode === "admin")
								}
								options={[
									{
										label: t(
											"projects.permissions.permission-mode-admin",
										),
										value: "admin",
									},
									{
										label: t(
											"projects.permissions.permission-mode-custom",
										),
										value: "custom",
									},
								]}
							/>
						)}

						{isProjectAdmin ? (
							<Alert variant="info" className="mb-0">
								{t("projects.permissions.admin-full-access")}
							</Alert>
						) : (
							<>
								<PermissionPresetSelector
									projectId={projectId}
									appliedPermissions={permissions}
									onPresetApplied={({
										presetUsedName: name,
										permissions: perms,
									}) => {
										setPresetUsedName(name);
										setPermissions(perms);
									}}
								/>
								<PermissionsForm
									projectId={projectId}
									permissions={permissions}
									onChange={setPermissions}
								/>
							</>
						)}
					</>
				)
			}
		/>
	);
}

export default EditPermissionsModal;
